import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  saveSession, loadSession, clearSession,
  getTokenFromUrl, buildAuthUrl
} from '@/lib/authUtils';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize: check URL token first, then localStorage
  useEffect(() => {
    const urlToken = getTokenFromUrl();
    const session = loadSession();

    const activeToken = urlToken || session?.token;

    if (activeToken) {
      if (urlToken) {
        // New token from cross-domain relay — save it
        saveSession(activeToken);
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${activeToken}`;
      setToken(activeToken);
      fetchUser(activeToken);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (t) => {
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      saveSession(t, response.data);
    } catch {
      // Token invalid — clear everything
      performLogout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;

    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    saveSession(access_token, userData);

    return userData;
  };

  const register = async (name, email, phone, password, role = 'customer') => {
    const response = await axios.post(`${API}/auth/register`, {
      name, email, phone, password, role
    });
    const { access_token, user: userData } = response.data;

    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    saveSession(access_token, userData);

    return userData;
  };

  const performLogout = useCallback(() => {
    clearSession();
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  const logout = performLogout;

  // Build a URL to another domain that carries the auth session
  const crossDomainUrl = useCallback((url) => {
    return buildAuthUrl(url, token);
  }, [token]);

  const isAdmin = () => user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = () => user?.role === 'super_admin';
  const isAffiliate = () => user?.role === 'affiliate' || isAdmin();

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      login, register, logout,
      isAdmin, isSuperAdmin, isAffiliate,
      crossDomainUrl
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
